import * as customerActions from './customerReducer';
import Axios from 'axios';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';
import queryString from '../../../helpers/queryString';
import handleErrorResponse from '../../../helpers/handleErrorResponse';
import { cloneDeep, get, isEmpty } from 'lodash';
import moment from 'moment';
import { ADD_RESOURCE } from '../../layout/redux/breadcrumb';

const defaultIncludes = [];

const toApi = (customer) => {
  customer.data.attributes.dob = dateToApi(customer.data.attributes.dob);

  return customer;
};

const fromApi = (customer) => {
  customer.data.attributes.dob = dateFromApi(customer.data.attributes.dob);
  if (isEmpty(get(customer, 'data.attributes.marketing_options'))) {
    customer.data.attributes.marketing_options = {
      post: -1,
      email: -1,
      sms: -1,
      phone: -1,
    };
  }

  return customer;
};

const setCustomer = (customer) => {
  return {
    type: customerActions.SET_CUSTOMER,
    data: fromApi(customer),
  };
};

const dateToApi = (date) => {
  if (!date) {
    return '';
  }

  const formattedDate = moment(date, 'DD/MM/YYYY')
    .utcOffset(0, true)
    .format();

  return formattedDate !== 'Invalid Date' ? formattedDate : '';
};

const dateFromApi = (date) => {
  if (!date) {
    return '';
  }

  const formattedDate = moment(date)
    .utcOffset(0, true)
    .format('DD/MM/YYYY');

  return formattedDate !== 'Invalid Date' ? formattedDate : '';
};

const clearData = () => {
  return {
    type: customerActions.CLEAR_DATA,
  };
};

const isSubmitting = (status) => {
  return {
    type: customerActions.IS_SUBMITTING,
    status,
  };
};

const submitSucceeded = (status) => {
  return {
    type: customerActions.SUBMIT_SUCCEEDED,
    status,
  };
};

const getCustomer = (id, includes = defaultIncludes) => {
  return (dispatch) => {
    let endpoint = 'customers/' + id;
    endpoint += queryString(includes);

    return Axios.get(endpoint).then((response) => {
      dispatch(setCustomer(response.data));
      dispatch({
        type: ADD_RESOURCE,
        id: response.data.data.id,
        name: response.data.data.attributes.customer_name,
      });
      return true;
    });
  };
};

const createCustomer = (resource) => {
  return (dispatch) => {
    dispatch(isSubmitting(true));
    return Axios.post('customers', resource)
      .then((response) => {
        dispatch(isSubmitting(false));
        dispatch(push('/customers/' + response.data.data.id));
        return true;
      })
      .catch((error) => {
        dispatch(isSubmitting(false));
        handleErrorResponse(error, 'There was an error creating the customer');
      });
  };
};

const updateCustomer = (resource, includes = defaultIncludes) => {
  const clonedResource = cloneDeep(resource);
  return (dispatch) => {
    dispatch(isSubmitting(true));
    const id = clonedResource.data.id;
    let endpoint = 'customers/' + id;
    endpoint += queryString(includes);

    return Axios.patch(endpoint, toApi(clonedResource))
      .then(() => {
        dispatch(isSubmitting(false));
        dispatch(push('/customers/' + id));
        return true;
      })
      .catch((error) => {
        dispatch(isSubmitting(false));
        handleErrorResponse(error, 'There was an error updating the customer');
      });
  };
};

const markCustomerDeceased = (id) => {
  return (dispatch) => {
    dispatch(isSubmitting(true));
    const endpoint = `customers/${id}:mark-deceased`;
    return Axios.post(endpoint)
      .then((response) => {
        dispatch(setCustomer(response.data));
      })
      .catch((error) => {
        handleErrorResponse(error, 'There was an error updating the customer');
      })
      .finally(() => {
        dispatch(isSubmitting(false));
      });
  };
};

const deactivateCustomerAccount = (id) => {
  return (dispatch) => {
    dispatch(isSubmitting(true));
    const endpoint = `customers/${id}:deactivate-account`;
    return Axios.post(endpoint)
      .then((response) => {
        dispatch(setCustomer(response.data));
      })
      .catch((error) => {
        handleErrorResponse(error, 'There was an error updating the customer');
      })
      .finally(() => {
        dispatch(isSubmitting(false));
      });
  };
};

const mergeCustomers = (id, resource) => {
  return (dispatch) => {
    dispatch(isSubmitting(true));
    return Axios.post('customers/' + id + ':merge-accounts', resource)
      .then(() => {
        dispatch(isSubmitting(false));
        toastr.success('Success', 'Customer accounts merged');
        return true;
      })
      .catch((error) => {
        dispatch(isSubmitting(false));
        handleErrorResponse(error, 'There was an error merging the customer accounts');
      });
  };
};

const refundCustomerTransaction = (transactionId, resource) => {
  return (dispatch) => {
    dispatch(isSubmitting(true));
    dispatch(submitSucceeded(false));
    return Axios.post('policies/transactions/' + transactionId + ':refund', resource)
      .then(() => {
        toastr.success('Success', 'Transaction Refunded');
        dispatch(isSubmitting(false));
        dispatch(submitSucceeded(true));
        return true;
      })
      .catch((error) => {
        dispatch(isSubmitting(false));
        dispatch(submitSucceeded(false));
        handleErrorResponse(error, 'There was an error refunding transaction');
      });
  };
};

const anonymizeCustomer = (customerId) => {
  return (dispatch) => {
    dispatch(isSubmitting(true));

    return Axios.post(`/customers/${customerId}:anonymize`)
      .then(() => {
        toastr.success('Success', 'Customer Anonymized');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'There was an error');
      })
      .finally(() => {
        dispatch(isSubmitting(false));
      });
  };
};

export const actions = {
  anonymizeCustomer,
  clearData,
  isSubmitting,
  submitSucceeded,
  getCustomer,
  createCustomer,
  updateCustomer,
  mergeCustomers,
  refundCustomerTransaction,
  markCustomerDeceased,
  deactivateCustomerAccount,
};
